/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { HourlyForecastProviderV6 } from '../providers/hourlyForecastProviderV6';
import { AsyncAction } from '../../base-classes/asyncAction';
import { mapParamsToObject } from '../../utilities/mapParamsToObject';
import { TimeMS } from '../../utilities';
import { temperatureUnits, pressureUnits, speedUnits, lengthUnits, lengthUnitsRates } from '../../utilities/units';

export const GET_HOURLY_FORECAST_BY_LATLON_V6 = new AsyncAction('GET_HOURLY_FORECAST_BY_LATLON_V6');
export const GET_FULL_HOURLY_FORECAST_V6 = new AsyncAction('GET_FULL_HOURLY_FORECAST_V6');
export const GET_ZERO_HOUR_FORECAST_V6 = new AsyncAction('GET_ZERO_HOUR_FORECAST_V6');

interface hourlyForecastParams {
    location: string;
    locationType: string;
    offset: number;
    length: number;
    baseUrl: string;
    authId: string;
    secret: string;
    stdParameters: Record<string, unknown>;
    verbose: boolean;
    refresh: boolean;
    temperatureUnit: number;
    pressureUnit: number;
    speedUnit: number;
    precipitationUnit: number;
    precipitationRateUnit: string;
    roundingProfile: string;
    cacheDuration: number;
}

export function getHourlyForecastByLatLon(...params) {
    let paramsObject: hourlyForecastParams = {
        location: undefined,
        locationType: undefined,
        offset: undefined,
        length: undefined,
        baseUrl: undefined,
        authId: undefined,
        secret: undefined,
        stdParameters: undefined,
        verbose: undefined,
        refresh: undefined,
        temperatureUnit: undefined,
        pressureUnit: undefined,
        speedUnit: undefined,
        precipitationUnit: undefined,
        precipitationRateUnit: undefined,
        roundingProfile: undefined,
        cacheDuration: undefined
    };
    paramsObject = mapParamsToObject(params, paramsObject);

    return {
        type: GET_HOURLY_FORECAST_BY_LATLON_V6.base,
        payload: getHourlyForecast(paramsObject)
    };
}

/*
- This function is used by current conditions action as well.
- This function should not be used from outside the common-js library.
- This is the only function in this file that actually calls the privider/API.
- All other functions in this file call this function. 
*/
export async function getHourlyForecast({
    location,
    locationType,
    offset,
    length,
    baseUrl,
    authId,
    secret,
    stdParameters,
    verbose,
    refresh,
    temperatureUnit,
    pressureUnit,
    speedUnit,
    precipitationUnit,
    precipitationRateUnit,
    roundingProfile,
    cacheDuration
}) {
    const op = new HourlyForecastProviderV6(baseUrl, authId, secret);
    return await op.getHourlyForecast(
        location,
        locationType,
        offset,
        length,
        stdParameters,
        verbose,
        refresh,
        temperatureUnit,
        pressureUnit,
        speedUnit,
        precipitationUnit,
        precipitationRateUnit,
        roundingProfile,
        cacheDuration
    );
}

/*
NOTE: this function does not seem to be used anywhere, mobile or Web.
Consider deleting.
*/
export function getZeroHourForecast(...params) {
    let paramsObject: hourlyForecastParams = {
        location: undefined,
        locationType: undefined,
        offset: undefined,
        length: undefined,
        baseUrl: undefined,
        authId: undefined,
        secret: undefined,
        stdParameters: undefined,
        verbose: undefined,
        refresh: undefined,
        temperatureUnit: undefined,
        pressureUnit: undefined,
        speedUnit: undefined,
        precipitationUnit: undefined,
        precipitationRateUnit: undefined,
        roundingProfile: undefined,
        cacheDuration: undefined
    };

    paramsObject = mapParamsToObject(params, paramsObject);

    paramsObject.offset = paramsObject.offset || 0;
    paramsObject.length = paramsObject.length || 3;

    return {
        type: GET_ZERO_HOUR_FORECAST_V6.base,
        payload: getHourlyForecast(paramsObject)
    };
}

export const zeroHourRequestParams = {
    offset: 0,
    length: 2,
    cacheDuration: TimeMS.minute * 2.5
};

export const firstSecondHourRequestParams = {
    offset: 1,
    length: 3,
    cacheDuration: TimeMS.minute * 5
};

export const fullHourlyRequestParams = {
    offset: 0,
    length: 144,
    cacheDuration: TimeMS.minute * 15
};

export const defaultRequestParamsList = [
    zeroHourRequestParams,
    firstSecondHourRequestParams,
    fullHourlyRequestParams
];

export function getFullHourlyForecastV6({
    location,
    locationType,
    requestParamsList,
    baseUrl,
    authId,
    secret,
    stdParameters,
    verbose,
    refresh,
    temperatureUnit = temperatureUnits.fahrenheit,
    pressureUnit = pressureUnits.inches,
    speedUnit = speedUnits.mph,
    precipitationUnit = lengthUnits.inches,
    precipitationRateUnit = lengthUnitsRates.centimeters,
    roundingProfile = 'DisplayV1'
}) {

    requestParamsList = requestParamsList || defaultRequestParamsList;
    return async (dispatch) => {
        try {
            dispatch({ type: GET_FULL_HOURLY_FORECAST_V6.pending });
            const values = await Promise.all(requestParamsList.map(({ offset, length, cacheDuration }) => {
                return getHourlyForecast({
                    location,
                    locationType,
                    offset,
                    length,
                    baseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    temperatureUnit,
                    pressureUnit,
                    speedUnit,
                    precipitationUnit,
                    precipitationRateUnit,
                    roundingProfile,
                    cacheDuration
                });
            }));
            dispatch({ type: GET_FULL_HOURLY_FORECAST_V6.fulfilled, payload: values });
        } catch (error) {
            dispatch({ type: GET_FULL_HOURLY_FORECAST_V6.rejected, payload: error });
        }
    };
}
